import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ai-content-hub/content-kit';

import { SpeakingAiGradePanel } from './SpeakingAiGradePanel';
import { SpeakingPronunciationPanel } from './SpeakingPronunciationPanel';
import {
    DEFAULT_SPEAKING_GRADING_QUESTION_TYPE,
    SPEAKING_GRADING_QUESTION_TYPES,
    SPEAKING_QUESTION_TYPES_WITH_PICTURE,
} from './schema';

/**
 * EAA-1890: a sample SpeechAce body for the Read Aloud example — the same shape
 * `normalizePronunciationScore` reads from the real endpoint, so the dialog and the
 * learner's panel render through identical code. Sample numbers only.
 */
function buildReadAloudSample(answer: string): Record<string, unknown> {
    const words = answer.split(/\s+/).filter(Boolean);
    const qualities = [96, 88, 74, 91, 42, 83];
    return {
        pronunciation: 82,
        details: {
            text_score: {
                pte_score: { pronunciation: 82 },
                word_score_list: words.map((word, i) => ({
                    word: word.replace(/[.,!?]$/, ''),
                    quality_score: qualities[i % qualities.length],
                    phone_score_list: [],
                })),
            },
        },
    };
}

export interface SpeakingGradingExampleDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The `QT_SPEAK_*` code whose example to show. */
    questionType: string;
    t: (key: string, fallback?: string) => string;
}

/**
 * EAA-1890: shows what one grading type actually does — the request posted to
 * `/api/v3/scoring/speaking` and the graded result that comes back.
 *
 * The dropdown's one-line example says what the learner is asked to do; it cannot
 * show what grading produces. Authors were choosing between seven PTE-style labels
 * with no way to see that "Describe Image" needs a picture on the page, or that the
 * result carries a transcript and per-criterion scores. The output half is rendered
 * by `SpeakingAiGradePanel` — the same component the learner sees after submitting —
 * so the example cannot drift from the real thing.
 *
 * Sample numbers only. Nothing here calls the grading API.
 */
export const SpeakingGradingExampleDialog: React.FC<SpeakingGradingExampleDialogProps> = ({
    open,
    onOpenChange,
    questionType,
    t,
}) => {
    const type =
        SPEAKING_GRADING_QUESTION_TYPES.find((entry) => entry.code === questionType) ??
        SPEAKING_GRADING_QUESTION_TYPES[0];

    const needsPicture = (SPEAKING_QUESTION_TYPES_WITH_PICTURE as readonly string[]).includes(
        type.code
    );
    const prompt = t(type.examplePromptKey);
    const answer = t(type.exampleAnswerKey);

    // EAA-1890: Read Aloud posts to the SpeechAce endpoint instead, whose request is the
    // reference script plus the recording — no question_type, no picture.
    const isReadAloud = type.code === DEFAULT_SPEAKING_GRADING_QUESTION_TYPE;

    const inputRows: { label: string; value: string; mono?: boolean }[] = isReadAloud
        ? [
              { label: 'text', value: prompt },
              {
                  label: t('speakingGradingExampleFieldAudio'),
                  value: t('speakingGradingExampleFieldAudioValue'),
              },
          ]
        : [
              { label: 'question_type', value: type.code, mono: true },
              { label: t('speakingGradingExampleFieldPrompt'), value: prompt },
              {
                  label: t('speakingGradingExampleFieldAudio'),
                  value: t('speakingGradingExampleFieldAudioValue'),
              },
          ];
    if (!isReadAloud && needsPicture) {
        inputRows.push({
            label: t('speakingGradingExampleFieldPicture'),
            value: t('speakingGradingExampleFieldPictureValue'),
        });
    }

    // Shape mirrors what `scorer.ts` stores in `details` after a grading job, so the
    // panel below renders it exactly as it renders a real result.
    const sampleDetails: Record<string, unknown> = {
        aiGrading: true,
        questionType: type.code,
        apiResponses: [
            {
                itemId: 'example',
                result: {
                    overall_score: 82,
                    word_count: answer.split(/\s+/).filter(Boolean).length,
                    transcript: answer,
                    criteria_scores: {
                        pronunciation: { score: 85, max: 100 },
                        fluency: { score: 80, max: 100 },
                        content: { score: 81, max: 100 },
                    },
                    detailed_feedback: {
                        strengths: [t('speakingGradingExampleStrength')],
                        weaknesses: [t('speakingGradingExampleWeakness')],
                    },
                },
            },
        ],
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="speaking-grading-example-dialog max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {`${t('speakingGradingExampleTitle')} — ${type.name}`}
                    </DialogTitle>
                </DialogHeader>

                <div className="speaking-grading-example-body max-h-[70vh] space-y-6 overflow-y-auto">
                    <section className="speaking-grading-example-input space-y-2">
                        <h4 className="text-sm font-semibold text-foreground">
                            {t('speakingGradingExampleInput')}
                        </h4>
                        <dl className="space-y-2">
                            {inputRows.map((row) => (
                                <div
                                    key={row.label}
                                    className="speaking-grading-example-row rounded-sm bg-muted/30 p-2 border border-border"
                                >
                                    <dt className="text-xs font-medium text-muted-foreground">
                                        {row.label}
                                    </dt>
                                    <dd
                                        className={
                                            row.mono
                                                ? 'text-sm font-mono text-foreground'
                                                : 'text-sm text-foreground'
                                        }
                                    >
                                        {row.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </section>

                    <section className="speaking-grading-example-output space-y-2">
                        <h4 className="text-sm font-semibold text-foreground">
                            {t('speakingGradingExampleOutput')}
                        </h4>
                        {isReadAloud ? (
                            <SpeakingPronunciationPanel
                                details={{
                                    pronunciationGrading: true,
                                    pronunciationResults: [
                                        {
                                            itemId: 'example',
                                            referenceText: answer,
                                            result: buildReadAloudSample(answer),
                                        },
                                    ],
                                }}
                                items={[]}
                                t={t}
                            />
                        ) : (
                            <SpeakingAiGradePanel details={sampleDetails} items={[]} t={t} />
                        )}
                        <p className="text-xs text-muted-foreground">
                            {t('speakingGradingExampleTranscriptNote')}
                        </p>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
};
